import { useMemo, useState } from 'react';

import { useWorkforceSession } from '@/auth/use-workforce-session';
import { AppRow } from '@/components/dashboard/app-row';
import { EnvFilter, type EnvFilterValue } from '@/components/dashboard/env-filter';
import { Header } from '@/components/dashboard/header';
import { PinnedSection } from '@/components/dashboard/pinned-section';
import { useCatalog } from '@/hooks/use-catalog';
import { usePins } from '@/hooks/use-pins';
import { ENVIRONMENTS } from '@/types/catalog';

const lastSignIn = (authTime: number) => {
  const minutes = Math.round((Date.now() / 1000 - authTime) / 60);
  if (minutes < 60) {
    return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  }
  const hours = Math.round(minutes / 60);
  if (hours < 24) {
    return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  }
  return new Date(authTime * 1000).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
};

export const DashboardPage = () => {
  const session = useWorkforceSession();
  const [search, setSearch] = useState('');
  const [envFilter, setEnvFilter] = useState<EnvFilterValue>('all');
  const { pins, togglePin } = usePins();

  const groups = session?.groups ?? [];
  const { apps: entitled, loading, error } = useCatalog(groups, session?.email ?? '');

  const searched = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) {
      return entitled;
    }
    return entitled.filter((app) => `${app.label} ${app.description}`.toLowerCase().includes(query));
  }, [entitled, search]);

  const pinned = searched.filter((app) => pins.includes(app.id));
  const tools = searched.filter((app) => app.section === 'tools');
  const allEnvironments = searched.filter((app) => app.section === 'environments');
  const environments = allEnvironments.filter((app) => envFilter === 'all' || app.environment === envFilter);

  const envCounts = {
    all: allEnvironments.length,
    ...Object.fromEntries(ENVIRONMENTS.map((environment) => [environment, allEnvironments.filter((app) => app.environment === environment).length])),
  } as Record<EnvFilterValue, number>;

  if (!session) {
    return null;
  }

  return (
    <div className="flex min-h-dvh flex-col bg-background text-foreground">
      <Header session={session} search={search} onSearchChange={setSearch} />

      <main className="mx-auto w-full max-w-7xl flex-1 space-y-10 px-4 py-8 sm:px-6 lg:px-8">
        {error && (
          <p className="rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-amber-900 text-sm dark:text-amber-100" role="status">
            {error}
          </p>
        )}

        {loading ? (
          <p className="text-muted-foreground text-sm">Loading apps…</p>
        ) : (
          <>
            <PinnedSection apps={pinned} />

            <section aria-labelledby="tools-heading">
              <h2 id="tools-heading" className="font-medium text-muted-foreground text-xs uppercase tracking-widest">
                Tools <span className="ml-1 text-muted-foreground/60 normal-case">{tools.length}</span>
              </h2>
              {tools.length > 0 ? (
                <div className="mt-3 grid grid-cols-1 gap-x-2 rounded-2xl border bg-card p-2 md:grid-cols-2 xl:grid-cols-3">
                  {tools.map((app) => (
                    <AppRow key={app.id} app={app} isPinned={pins.includes(app.id)} onTogglePin={togglePin} />
                  ))}
                </div>
              ) : (
                <p className="mt-3 text-muted-foreground text-sm">{search ? `No tools match “${search}”.` : 'No tools entitled for your account.'}</p>
              )}
            </section>

            <section aria-labelledby="environments-heading">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h2 id="environments-heading" className="font-medium text-muted-foreground text-xs uppercase tracking-widest">
                  Environments <span className="ml-1 text-muted-foreground/60 normal-case">{environments.length}</span>
                </h2>
                <EnvFilter value={envFilter} counts={envCounts} onChange={setEnvFilter} />
              </div>
              {environments.length > 0 ? (
                <div className="mt-3 grid grid-cols-1 gap-x-2 rounded-2xl border bg-card p-2 md:grid-cols-2 xl:grid-cols-3">
                  {environments.map((app) => (
                    <AppRow key={app.id} app={app} isPinned={pins.includes(app.id)} onTogglePin={togglePin} />
                  ))}
                </div>
              ) : (
                <p className="mt-3 text-muted-foreground text-sm">No environment links here. Try another environment or clear the search.</p>
              )}
            </section>
          </>
        )}
      </main>

      <footer>
        <div className="mx-auto flex w-full max-w-7xl flex-wrap items-center justify-between gap-2 px-4 py-6 text-muted-foreground text-xs sm:px-6 lg:px-8">
          <span>Last sign in: {lastSignIn(session.authTime)}</span>
          <span>Internal SSO — Keycloak</span>
        </div>
      </footer>
    </div>
  );
};
